import { AsyncPipe } from '@angular/common';
import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Award } from '../../../../models/award.school.model';
import { Group } from '../../../../models/group.model';
import { SchoolMemberType } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import * as awardActions from '../../../../state/actions/award.school.actions';
import { AppState } from '../../../../state/appstate';
import * as awardSelector from '../../../../state/selectors/award.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { DlgUpsertAwardComponent } from '../dlg-upsert-award/dlg-upsert-award.component';

@Component({
  selector: 'app-list-awards',
  standalone: true,
  imports: [
    BtnMoreComponent,
    BtnFabComponent,
    MomentPipe,
    AsyncPipe,
    InitialsPipe,
  ],
  templateUrl: './list-awards.component.html',
  styleUrl: './list-awards.component.css',
})
export class ListAwardsComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  private dialog: MatDialog = inject(MatDialog);
  private afs: AngularFirestore = inject(AngularFirestore);

  awards: Award[] | null | undefined;

  loading$: Observable<boolean> = new Observable();
  hasMore$: Observable<boolean> = new Observable();

  studentId = input<string>();
  fetchAllGroups = input<boolean>(false);
  showGroupName = input<boolean>(false);

  groupIdInput = input<string | null>(null, { alias: 'groupId' });
  routeGroupId: string | null = null;

  get groupId(): string | null {
    return this.groupIdInput() || this.routeGroupId;
  }

  group: Group | null | undefined;
  groupNames: { [groupId: string]: string } = {};

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  rolesType = SchoolRoles;

  myRoles$: Observable<any> = new Observable();

  exit$: Subject<void> = new Subject<void>();

  constructor(private store: Store<AppState>) {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.routeGroupId = params.get('id');
    });

    this.loading$ = this.store.select(awardSelector.loading);
    this.hasMore$ = this.store.select(awardSelector.hasMore);

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.store.select(groupSelector.getSelectedGroup).subscribe((group) => {
      this.group = group;
    });
  }

  ngOnInit() {
    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    if (!this.group && this.groupId) {
      this.store
        .select(groupSelector.getGroupById(this.groupId))
        .pipe(takeUntil(this.exit$))
        .subscribe((group) => {
          this.group = group;
        });
    }

    const selector = this.fetchAllGroups()
      ? awardSelector.getAllAwardsByStudentId(this.studentId())
      : awardSelector.getAwardsByStudentId(this.studentId());

    this.store
      .select(selector)
      .pipe(takeUntil(this.exit$))
      .subscribe((awards) => {
        this.awards = awards;
        if (this.fetchAllGroups() || this.showGroupName()) {
          this.loadGroupNames(awards || []);
        }
      });

    if ((this.awards ?? []).length === 0 && this.studentId()) {
      this.fetchInitialAwards();
    }
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  fetchInitialAwards() {
    if (!this.studentId()) return;

    if (this.fetchAllGroups()) {
      this.store.dispatch(
        awardActions.getAwards({
          studentId: this.studentId(),
          allGroups: true,
          limit: 10,
        }),
      );
    } else if (this.groupId && this.group?.academicYear) {
      this.store.dispatch(
        awardActions.getAwards({
          groupId: this.groupId,
          academicYear: this.group.academicYear,
          studentId: this.studentId(),
          limit: 10,
        }),
      );
    }
  }

  loadGroupNames(awards: Award[]) {
    const groupIds = [
      ...new Set(
        awards
          .map((a) => a.groupId)
          .filter((id): id is string => Boolean(id)),
      ),
    ];
    groupIds.forEach((gId) => {
      if (!this.groupNames[gId]) {
        this.afs
          .collection('groups')
          .doc(gId)
          .get()
          .pipe(takeUntil(this.exit$))
          .subscribe((snap) => {
            if (snap.exists) {
              const data = snap.data() as any;
              this.groupNames[gId] = data?.name || 'Unknown Group';
            }
          });
      }
    });
  }

  onEditAward(item: Award) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Edit Award',
      description: 'Edit Award Details',
      action: 'Update',
      groupId: this.groupId,
      academicYear: this.group?.academicYear,
      studentId: this.studentId(),
      studentName: item.studentName,
      studentPhotoUrl: item.studentPhotoUrl,
      award: item,
    };

    const dialogRef = this.dialog.open(DlgUpsertAwardComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      // console.log('onEditAward Completed');
      return;
    });
  }

  addMore() {
    const lastModified =
      (this.awards ?? [])?.length > 0
        ? this.awards![this.awards!.length - 1].modified
        : undefined;

    if (this.fetchAllGroups()) {
      this.store.dispatch(
        awardActions.getAwards({
          studentId: this.studentId(),
          allGroups: true,
          limit: 10,
          lastRecord: lastModified,
        }),
      );
    } else {
      this.store.dispatch(
        awardActions.getAwards({
          groupId: this.groupId!,
          studentId: this.studentId(),
          academicYear: this.group?.academicYear!,
          limit: 10,
          lastRecord: lastModified,
        }),
      );
    }
  }

  refresh() {
    this.fetchInitialAwards();
  }

  canDelete(item: Award, roles: any): boolean {
    const isManagementOrAdmin = Boolean(roles?.management || roles?.admin);
    if (!isManagementOrAdmin) {
      return false;
    }
    const currentGroupId = this.groupId;
    if (!currentGroupId) {
      return false;
    }
    if (this.fetchAllGroups()) {
      return item.groupId === currentGroupId;
    }
    return !item.groupId || item.groupId === currentGroupId;
  }

  onDelete(item: Award) {
    const targetGroupId = item.groupId || this.groupId;
    if (!targetGroupId || !item?.id) return;

    return this.store.dispatch(
      awardActions.deleteAction({
        groupId: targetGroupId,
        id: item.id,
      }),
    );
  }
}
